import { formatDurationMs } from './format-duration'

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR
const MONTH = 30 * DAY

export interface RelativeTimeOptions {
  /** Reference "now" in epoch ms. Defaults to `Date.now()`. */
  now?: number
  /** Returned when the timestamp is null / undefined / unparseable. Defaults to ''. */
  fallback?: string
  /** Keep hours and minutes instead of rounding to a single unit. */
  precision?: 'unit' | 'minute'
}

/**
 * Canonical relative-time label for the ontology UI. Renders a compact
 * "X ago" (past) or "in X" (future) string from an epoch-ms number or an ISO
 * string. Sub-minute deltas render as "just now" (past) / "in <1m" (future);
 * buckets are minutes → hours → days → months. Direction is inferred from the
 * sign of `now - ts`.
 */
export function formatRelativeTime(
  ts: number | string | null | undefined,
  { now = Date.now(), fallback = '', precision = 'unit' }: RelativeTimeOptions = {}
): string {
  if (ts === null || ts === undefined) return fallback
  const t = typeof ts === 'number' ? ts : Date.parse(ts)
  if (!Number.isFinite(t)) return fallback

  const deltaMs = now - t
  const future = deltaMs < 0
  const abs = Math.abs(deltaMs)

  if (abs < MINUTE) return future ? 'in <1m' : 'just now'

  if (precision === 'minute') {
    const duration = formatDurationMs(Math.floor(abs / MINUTE) * MINUTE)
    return future ? `in ${duration}` : `${duration} ago`
  }

  let value: number
  let unit: string
  if (abs < HOUR) {
    value = Math.floor(abs / MINUTE)
    unit = 'm'
  } else if (abs < DAY) {
    value = Math.floor(abs / HOUR)
    unit = 'h'
  } else if (abs < MONTH) {
    value = Math.floor(abs / DAY)
    unit = 'd'
  } else {
    value = Math.floor(abs / MONTH)
    unit = 'mo'
  }

  return future ? `in ${value}${unit}` : `${value}${unit} ago`
}

/**
 * Compact wall-clock label for a chat message timestamp (e.g. "Aug 20, 8:00 AM").
 * Uses the viewer's locale. Returns `fallback` when the input is missing or
 * unparseable.
 */
export function formatCompactLocalTime(
  ts: number | string | null | undefined,
  { fallback = '' }: { fallback?: string } = {}
): string {
  if (ts === null || ts === undefined) return fallback
  const t = typeof ts === 'number' ? ts : Date.parse(ts)
  if (!Number.isFinite(t)) return fallback
  return new Date(t).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  })
}

/**
 * Format an agent-emitted event window (ISO-8601 UTC strings, or epoch ms)
 * as an operator-facing UTC 24-hour label — the timeframe the floor should
 * investigate (e.g. to pull camera footage). Same UTC day collapses the end
 * to a time: `2026-06-26 02:55–13:00 UTC`; cross-day keeps both dates:
 * `2026-06-26 02:55 → 06-27 01:00 UTC`. Returns '' when `start` is
 * missing/unparseable, and just the start when `end` is absent.
 */
export function formatEventWindowUTC(
  start: number | string | null | undefined,
  end?: number | string | null | undefined
): string {
  const s = typeof start === 'number' ? start : start ? Date.parse(start) : NaN
  if (!Number.isFinite(s)) return ''
  const dateTime = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'UTC',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23'
  })
  const timeOnly = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23'
  })
  const fmtDT = (ms: number): string => dateTime.format(ms).replace(', ', ' ')
  const startStr = fmtDT(s)
  const e = typeof end === 'number' ? end : end ? Date.parse(end) : NaN
  if (!Number.isFinite(e)) return `${startStr} UTC`
  const sameDay = new Date(s).toISOString().slice(0, 10) === new Date(e).toISOString().slice(0, 10)
  return sameDay ? `${startStr}–${timeOnly.format(e)} UTC` : `${startStr} → ${fmtDT(e)} UTC`
}
